/**
 * voiceParser.js
 * Parse ucapan Indonesia → daftar {name, quantity, unit}
 * Contoh: "nasi putih satu porsi tempe goreng dua potong susu satu gelas"
 *       → [{name:"nasi putih", qty:1, unit:"porsi"}, ...]
 */

// Indonesian number words → number
const NUMBER_WORDS = {
  nol: 0, satu: 1, dua: 2, tiga: 3, empat: 4, lima: 5,
  enam: 6, tujuh: 7, delapan: 8, sembilan: 9, sepuluh: 10,
  sebelas: 11, dua_belas: 12, 'dua belas': 12,
  setengah: 0.5, 'setengah': 0.5,
  seperempat: 0.25, 'tiga perempat': 0.75,
  satu_setengah: 1.5, 'satu setengah': 1.5,
  dua_setengah: 2.5, 'dua setengah': 2.5,
  seratus: 100, 'dua ratus': 200, 'tiga ratus': 300, 'empat ratus': 400,
  'lima ratus': 500, 'enam ratus': 600, 'tujuh ratus': 700,
  'delapan ratus': 800, 'sembilan ratus': 900, seribu: 1000,
  'dua ribu': 2000,
};

// Common units (sorted longest first to avoid partial match)
const UNITS = [
  'sdm', 'sdt', 'gelas', 'porsi', 'piring', 'mangkok', 'mangkuk',
  'potong', 'lembar', 'lembaran', 'biji', 'buah', 'cup', 'botol',
  'sachet', 'ml', 'cc', 'gram', 'gr', 'g', 'kaleng', 'pak', 'sendok',
  'slice', 'pieces', 'piece', 'pack',
  // Parenteral / clinical units
  'kolf', 'bag', 'flacon', 'ampul', 'vial', 'pouch', 'flash', 'kantong', 'plabottle',
];

// Separator words between food items
const SEPARATORS = [
  'dan', 'kemudian', 'lalu', 'juga', 'sama', 'terus', 'ditambah',
  'plus', 'serta', 'with', 'and', 'koma',
];

/**
 * Convert word to number, return null if not a number
 */
function wordToNumber(word) {
  const w = word.trim().toLowerCase();
  if (NUMBER_WORDS[w] !== undefined) return NUMBER_WORDS[w];
  const n = parseFloat(w);
  if (!isNaN(n)) return n;
  return null;
}

/**
 * Main parser: text → [{name, quantity, unit}]
 */
export function parseVoiceInput(text) {
  if (!text || typeof text !== 'string') return [];

  let normalized = text.toLowerCase().trim();

  // Replace spoken punctuation
  normalized = normalized
    .replace(/\bkoma\b/g, ',')
    .replace(/\btitik\b/g, '.')
    .replace(/[,،]/g, ' , ')
    .replace(/\s+/g, ' ');

  // Split on comma or separator words
  const sepPattern = new RegExp(
    `\\s*(?:,|\\b(?:${SEPARATORS.join('|')})\\b)\\s*`,
    'gi'
  );

  const segments = normalized.split(sepPattern).filter(s => s.trim().length > 0);

  const results = [];

  for (const segment of segments) {
    const parsed = parseSegment(segment.trim());
    if (parsed) results.push(parsed);
  }

  // If no separators used, try to split on unit boundaries
  if (results.length === 0) {
    const fallback = parseSegment(normalized);
    if (fallback) results.push(fallback);
  }

  return results;
}

/**
 * Parse a single segment like "nasi putih satu porsi"
 * Returns {name, quantity, unit} or null
 */
function parseSegment(segment) {
  if (!segment || segment.trim().length < 2) return null;

  let words = segment.trim().split(/\s+/);
  let quantity = 1;
  let unit = 'porsi';
  let nameWords = [];

  // Strategy: scan from the end for (unit)(number) or (number)(unit) pattern
  // Then the rest is the name

  // Try to find unit in the words
  let unitIndex = -1;
  let unitStr = null;

  // Check multi-word units first (sorted longest first)
  const multiWordUnits = ['tiga perempat', 'satu setengah', 'dua setengah', 'satu dan setengah'];
  for (const mwu of multiWordUnits) {
    const mwuWords = mwu.split(' ');
    for (let i = 0; i <= words.length - mwuWords.length; i++) {
      const slice = words.slice(i, i + mwuWords.length).join(' ');
      if (slice === mwu) {
        quantity = NUMBER_WORDS[mwu] || 1;
        words.splice(i, mwuWords.length);
        break;
      }
    }
  }

  // Find unit (single word)
  for (let i = words.length - 1; i >= 0; i--) {
    const w = words[i].toLowerCase();
    if (UNITS.includes(w)) {
      unitIndex = i;
      unitStr = w;
      break;
    }
  }

  if (unitIndex !== -1) {
    unit = unitStr;
    const remaining = [...words];
    remaining.splice(unitIndex, 1);
    words = remaining;

    // Now find number adjacent to unit position
    // Check the word that was just before the unit (now at unitIndex - 1 or unitIndex after splice)
    let numIdx = -1;
    for (let i = words.length - 1; i >= 0; i--) {
      const num = wordToNumber(words[i]);
      if (num !== null) {
        quantity = num;
        numIdx = i;
        break;
      }
    }

    if (numIdx !== -1) {
      words.splice(numIdx, 1);
    }

    nameWords = words;
  } else {
    // No explicit unit found – try to find number anywhere
    let numIdx = -1;
    for (let i = words.length - 1; i >= 0; i--) {
      const num = wordToNumber(words[i]);
      if (num !== null) {
        quantity = num;
        numIdx = i;
        break;
      }
    }

    if (numIdx !== -1) {
      words.splice(numIdx, 1);
    }

    nameWords = words;
  }

  const name = nameWords
    .join(' ')
    .trim()
    .replace(/\s+/g, ' ');

  if (!name || name.length < 2) return null;

  return {
    name: name.charAt(0).toUpperCase() + name.slice(1),
    quantity,
    unit,
  };
}

/**
 * Fuzzy match a parsed item (food/parenteral) name against the database list.
 * Returns the top-N matches scored by string similarity.
 */
export function matchFoodsFromDatabase(parsedName, allFoods, topN = 5) {
  if (!parsedName || !allFoods || allFoods.length === 0) return [];

  const query = parsedName.toLowerCase().trim();
  const cleanStr = str => (str || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const cleanQuery = cleanStr(query);

  const scored = allFoods.map(food => {
    const name = (food.name || '').toLowerCase().trim();
    const cleanName = cleanStr(name);
    let score = 0;

    // 1. Exact match
    if (name === query) {
      score = 100;
    } else if (cleanName && cleanQuery && cleanName === cleanQuery) {
      score = 98; // e.g. "ka-en 3b" vs "ka en 3b" or "b.fluid" vs "b fluid"
    }
    // 2. Starts with query
    else if (name.startsWith(query)) {
      score = 85;
    } else if (cleanName.startsWith(cleanQuery)) {
      score = 82;
    }
    // 3. Query starts with item name
    else if (query.startsWith(name)) {
      score = 75;
    } else if (cleanQuery.startsWith(cleanName)) {
      score = 72;
    }
    // 4. Contains query
    else if (name.includes(query)) {
      score = 65;
    } else if (cleanName.includes(cleanQuery)) {
      score = 62;
    }
    // 5. Query is contained in item name
    else if (query.includes(name)) {
      score = 55;
    }
    // 6. Word overlap score
    else {
      const queryWords = query.split(/[\s\-_.]+/).filter(Boolean);
      const nameWords = name.split(/[\s\-_.]+/).filter(Boolean);
      const overlap = queryWords.filter(qw => 
        nameWords.some(nw => nw.includes(qw) || qw.includes(nw))
      );
      score = Math.round((overlap.length / Math.max(queryWords.length, 1)) * 50);
    }

    return { food, score };
  });

  return scored
    .filter(s => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, topN)
    .map(s => s.food);
}

// Alias for clinical clarity
export const matchItemsFromDatabase = matchFoodsFromDatabase;

